import React, { useState, useEffect } from "react";

import { getUsersByRole } from "../../../services/api/core";

import {
  addHoursTo24TimeString,
  decimalToHoursMinutes,
} from "../../../utils/datetime";
import { calculateHoursBetweenTimes } from "../../../utils/timekeeping/shiftCalculations";

import { Button } from "../../../components/ui";
import { Trash } from "../../../components/icons/svg/outline";

/**
 * DailyHoursLogForm Component
 *
 * A comprehensive form component for logging worker hours on specific jobs.
 * Handles the complete hours logging workflow including:
 * - Dynamic worker selection from active crew
 * - Time entry with automatic hours calculation
 * - Multiple worker entries per job
 * - Form validation and submission
 * - Real-time total hours calculation
 *
 * The component manages its own internal state for better encapsulation and
 * provides callbacks for parent components to handle submission events.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Object} props.jobData - Job data with structure: { job: {...}, workers: [...] }
 * @param {Function} [props.onFormDataChange] - Optional callback function called when form data changes
 * @param {Function} [props.onSubmitSuccess] - Optional callback function called after successful submission
 *
 * @example
 * return (
 *   <DailyHoursLogForm
 *     jobData={selectedJob}
 *     onFormDataChange={(jobId, formData) => handleFormDataUpdate(jobId, formData)}
 *     onSubmitSuccess={() => console.log('Hours submitted successfully')}
 *   />
 * )
 */
const DailyHoursLogForm = ({ jobData, onFormDataChange }) => {
  // Internal state management
  const [workers, setWorkers] = useState([]);
  const [workerEntries, setWorkerEntries] = useState([]);

  // Extract and initialize worker entries from jobData
  useEffect(() => {
    if (jobData && jobData.workers) {
      // Convert workers to worker entries format
      // log_time_in and log_time_out only exist on data loaded from the DB
      // If not present, uses values from the dispatch schedule, as it is a new log
      const initialEntries =
        jobData.workers.length > 0
          ? jobData.workers.map((worker) => ({
              worker: worker.id.toString(),
              timeIn: worker.log_time_in
                ? worker.log_time_in
                : jobData.default_time?.slice(0, 5) || "07:00",
              timeOut: worker.log_time_out
                ? worker.log_time_out
                : addHoursTo24TimeString(
                    jobData.default_time?.slice(0, 5) || "03:00",
                    8
                  ) || "15:00",
              hours:
                worker.log_time_in && worker.log_time_out
                  ? decimalToHoursMinutes(
                      calculateHoursBetweenTimes(
                        worker.log_time_in,
                        worker.log_time_out
                      ),
                      true
                    )
                  : "8:00",
            }))
          : [{ worker: "", timeIn: "07:00", timeOut: "15:00", hours: "8:00" }];

      setWorkerEntries(initialEntries);
      onFormDataChange(jobData.id, initialEntries, "workers");
    }
  }, [jobData, onFormDataChange]);

  // Load workers data from API (for additional workers not in jobData)
  useEffect(() => {
    const loadWorkers = async () => {
      try {
        const workersData = await getUsersByRole({ role: 'crew', active: true });
        setWorkers(workersData || []);
      } catch (error) {
        console.error("Error loading workers:", error);
      } finally {
        // Optionally set loading state here if needed
      }
    };

    loadWorkers();
  }, []);

  // Calculate hours between time in and time out
  const calculateHours = (timeIn, timeOut) => {
    if (!timeIn || !timeOut) return "0.00";

    const [inHour, inMin] = timeIn.split(":").map(Number);
    const [outHour, outMin] = timeOut.split(":").map(Number);

    let inMinutes = inHour * 60 + inMin;
    let outMinutes = outHour * 60 + outMin;

    // Handle overnight shifts (time out is next day)
    if (outMinutes <= inMinutes) {
      outMinutes += 24 * 60; // Add 24 hours
    }

    const totalMinutes = outMinutes - inMinutes;
    const hours = (totalMinutes / 60).toFixed(2);

    return hours;
  };

  // Update worker entry
  const updateWorkerEntry = (index, field, value) => {
    const updatedEntries = [...workerEntries];
    updatedEntries[index][field] = value;

    // Auto-calculate hours when time in/out changes
    if (field === "timeIn" || field === "timeOut") {
      const timeIn = field === "timeIn" ? value : updatedEntries[index].timeIn;
      const timeOut =
        field === "timeOut" ? value : updatedEntries[index].timeOut;
      updatedEntries[index].hours = calculateHours(timeIn, timeOut);
    }

    setWorkerEntries(updatedEntries);
    // Notify parent component of data change
    onFormDataChange(jobData.id, updatedEntries, "workers");
  };

  // Add new worker entry row
  const addWorkerEntry = () => {
    setWorkerEntries([
      ...workerEntries,
      { worker: "", timeIn: "07:00", timeOut: "15:00", hours: "8.00" },
    ]);
  };

  // Remove worker entry row
  const removeWorkerEntry = (index) => {
    if (workerEntries.length > 1) {
      const updatedEntries = workerEntries.filter((_, i) => i !== index);
      setWorkerEntries(updatedEntries);
    }
    // Notify parent component of data change
    onFormDataChange(
      jobData.id,
      workerEntries.filter((_, i) => i !== index),
      "workers"
    );
  };

  // Calculate total hours for the day
  const totalHours = workerEntries
    .filter((entry) => entry.worker && entry.hours)
    .reduce((sum, entry) => sum + parseFloat(entry.hours || 0), 0)
    .toFixed(2);

  return (
    <div className={`rounded-lg shadow-md py-1 mb-2 bg-white`}>
      <h3 className="font-bold text-gray-800 text-center mb-1 ">
        {jobData.number} - {jobData.description}
      </h3>

      {/* Worker Entries Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-gray-100">
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-1 text-left">Crew</th>
              <th className="border border-gray-300 px-1 text-left">Time In</th>
              <th className="border border-gray-300 px-1 text-left">
                Time Out
              </th>
              <th className="border border-gray-300 px-1 text-left">Hours</th>
              <th className="border border-gray-300 px-1 text-center">
                Remove
              </th>
            </tr>
          </thead>
          <tbody className={`bg-gray-100`}>
            {workerEntries.map((entry, index) => (
              <tr key={index}>
                <td className="border border-gray-300 px-1">
                  <select
                    value={entry.worker}
                    onChange={(e) =>
                      updateWorkerEntry(index, "worker", e.target.value)
                    }
                    className="w-full px-1 border-none bg-transparent"
                  >
                    <option value="">Select worker...</option>
                    {workers.map((worker) => (
                      <option key={worker.id} value={worker.id}>
                        {worker.first} {worker.last}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="border border-gray-300 px-1">
                  <div className="flex items-center">
                    <input
                      type="time"
                      value={entry.timeIn}
                      onChange={(e) =>
                        updateWorkerEntry(index, "timeIn", e.target.value)
                      }
                      className="flex-1 px-1 border-none bg-transparent"
                    />
                  </div>
                </td>
                <td className="border border-gray-300 px-1">
                  <div className="flex items-center">
                    <input
                      type="time"
                      value={entry.timeOut}
                      onChange={(e) =>
                        updateWorkerEntry(index, "timeOut", e.target.value)
                      }
                      className="flex-1 px-1 border-none bg-transparent"
                    />
                  </div>
                </td>
                <td className="border border-gray-300 px-1 text-center font-medium">
                  {entry.hours}
                </td>
                <td className="border border-gray-300 px-1 text-center">
                  <button
                    onClick={() => removeWorkerEntry(index)}
                    disabled={workerEntries.length <= 1}
                    className={`p-1 rounded transition-colors ${
                      workerEntries.length <= 1
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer hover:text-red-600"
                    }`}
                    title={
                      workerEntries.length <= 1
                        ? "Cannot remove the last entry"
                        : "Remove worker"
                    }
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            <tr key="add-worker-row">
              <td colSpan="5" className="text-center p-0">
                <Button
                  onClick={addWorkerEntry}
                  variant="custom"
                  text="Add Worker"
                  icon="plus"
                  className="px-4 py-2 h-6 bg-gray-200 hover:bg-gray-400 text-gray-800 rounded-none"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Job Total Hours */}
      <div className="mt-1 text-center">
        <div className="font-bold">Job Total Hours: {totalHours}</div>
      </div>
    </div>
  );
};

export default DailyHoursLogForm;
